// A pull scanner for the XML inside an .xlsx. Not a DOM parser, and not a
// general-purpose one.
//
// `DOMParser` would do this in one line, and it is the wrong tool twice over: it
// does not exist in Node, so the tests could not run the same code the app does,
// and it builds a node object for every one of the 100,000 cells in a real
// worksheet when all we want is to walk past most of them.
//
// The dialect here is narrow on purpose — OOXML parts are machine-written, so
// there are no entities beyond the five standard ones, no DTDs to resolve and no
// namespaces to reconcile. What it must get right is the shape of a `<c>`: an
// element with attributes, holding text.

export type Attrs = Record<string, string>;

export interface XmlHandlers {
  /** Called for `<a>` and for `<a/>`. */
  open?: (name: string, attrs: Attrs) => void;
  /** Called for `</a>` and, immediately after `open`, for `<a/>`. */
  close?: (name: string) => void;
  /** Text between tags, entity-decoded. Never called with an empty string. */
  text?: (text: string) => void;
}

export function scanXml(xml: string, handlers: XmlHandlers): void {
  const n = xml.length;
  let i = 0;

  while (i < n) {
    const lt = xml.indexOf('<', i);
    if (lt < 0) {
      emitText(xml.slice(i), handlers);
      return;
    }
    if (lt > i) emitText(xml.slice(i, lt), handlers);

    // Declarations, comments and CDATA: skipped whole. CDATA's contents are
    // text, and a sheet with a `<![CDATA[` in it is not a thing Excel writes —
    // but a formula containing `<` would be escaped, not wrapped, so treating
    // the block as text is the safe reading either way.
    if (xml.startsWith('<!--', lt)) {
      const end = xml.indexOf('-->', lt + 4);
      i = end < 0 ? n : end + 3;
      continue;
    }
    if (xml.startsWith('<![CDATA[', lt)) {
      const end = xml.indexOf(']]>', lt + 9);
      const stop = end < 0 ? n : end;
      if (stop > lt + 9) handlers.text?.(xml.slice(lt + 9, stop));
      i = end < 0 ? n : end + 3;
      continue;
    }
    if (xml.startsWith('<?', lt) || xml.startsWith('<!', lt)) {
      const end = xml.indexOf('>', lt + 2);
      i = end < 0 ? n : end + 1;
      continue;
    }

    const gt = findTagEnd(xml, lt + 1);
    if (gt < 0) return;
    const body = xml.slice(lt + 1, gt);
    i = gt + 1;

    if (body.startsWith('/')) {
      handlers.close?.(body.slice(1).trim());
      continue;
    }

    const selfClosing = body.endsWith('/');
    const inner = selfClosing ? body.slice(0, -1) : body;
    const space = firstSpace(inner);
    const name = space < 0 ? inner.trim() : inner.slice(0, space);
    handlers.open?.(name, space < 0 ? {} : parseAttrs(inner, space));
    if (selfClosing) handlers.close?.(name);
  }
}

/** The `>` that ends a tag, skipping any inside a quoted attribute value. */
function findTagEnd(xml: string, from: number): number {
  let quote = '';
  for (let i = from; i < xml.length; i++) {
    const ch = xml[i]!;
    if (quote) {
      if (ch === quote) quote = '';
      continue;
    }
    if (ch === '"' || ch === "'") quote = ch;
    else if (ch === '>') return i;
  }
  return -1;
}

function firstSpace(s: string): number {
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    if (ch === 32 || ch === 9 || ch === 10 || ch === 13) return i;
  }
  return -1;
}

function parseAttrs(src: string, from: number): Attrs {
  const attrs: Attrs = {};
  let i = from;
  const n = src.length;
  while (i < n) {
    while (i < n && /\s/.test(src[i]!)) i++;
    const eq = src.indexOf('=', i);
    if (eq < 0) break;
    const name = src.slice(i, eq).trim();
    let j = eq + 1;
    while (j < n && /\s/.test(src[j]!)) j++;
    const quote = src[j];
    if (quote !== '"' && quote !== "'") break;
    const end = src.indexOf(quote, j + 1);
    if (end < 0) break;
    if (name) attrs[name] = decodeXml(src.slice(j + 1, end));
    i = end + 1;
  }
  return attrs;
}

function emitText(raw: string, handlers: XmlHandlers): void {
  if (raw === '' || handlers.text === undefined) return;
  handlers.text(decodeXml(raw));
}

/**
 * The five predefined entities plus numeric escapes. Excel writes `_x000D_` for
 * a carriage return inside a string, which is NOT an entity and is left alone —
 * shared-string reading deals with it.
 */
export function decodeXml(s: string): string {
  if (!s.includes('&')) return s;
  return s.replace(/&(?:#(\d+)|#[xX]([0-9a-fA-F]+)|(amp|lt|gt|quot|apos));/g, (all, dec, hex, name) => {
    if (dec) return String.fromCodePoint(Number(dec));
    if (hex) return String.fromCodePoint(parseInt(hex, 16));
    switch (name) {
      case 'amp': return '&';
      case 'lt': return '<';
      case 'gt': return '>';
      case 'quot': return '"';
      case 'apos': return "'";
      default: return all;
    }
  });
}
